import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const ServicesGridSection = () => {
  const [expandedService, setExpandedService] = useState(null);
  const navigate = useNavigate()

  const services = [
    {
      id: 1,
      icon: 'fas fa-chart-line',
      title: 'Strategic Planning',
      description: 'Comprehensive business strategy development and market analysis',
      shortDesc: 'Data-driven strategies to accelerate your business growth',
      details: [
        'Market Research & Analysis',
        'Competitive Intelligence',
        'Business Model Optimization',
        'Growth Strategy Development',
        'Performance Metrics & KPIs',
        'Risk Assessment & Mitigation'
      ]
    },
    {
      id: 2,
      icon: 'fas fa-users',
      title: 'Audience Engagement',
      description: 'Build meaningful connections with your target audience',
      shortDesc: 'Build meaningful connections with your target audience',
      details: [
        'Customer Persona Development',
        'Social Media Strategy',
        'Community Building',
        'Content Marketing',
        'Email Marketing Campaigns',
        'Customer Journey Mapping'
      ]
    },
    {
      id: 3,
      icon: 'fas fa-bullhorn',
      title: 'Campaign Management',
      description: 'End-to-end campaign execution and optimization',
      shortDesc: 'End-to-end campaign execution and optimization',
      details: [
        'Multi-Channel Campaigns',
        'A/B Testing & Optimization',
        'Performance Tracking',
        'Budget Management',
        'Creative Development',
        'ROI Analysis & Reporting'
      ]
    },
    {
      id: 4,
      icon: 'fas fa-code',
      title: 'Website Development',
      description: 'Custom web solutions built for performance and user experience',
      shortDesc: 'Custom web solutions built for performance and user experience',
      details: [
        'Custom Website Development',
        'E-commerce Solutions',
        'Progressive Web Apps',
        'API Integration',
        'Performance Optimization',
        'Security Implementation'
      ]
    },
    {
      id: 5,
      icon: 'fas fa-search',
      title: 'SEO & Digital Marketing',
      description: 'Comprehensive digital marketing and search engine optimization',
      shortDesc: 'Comprehensive digital marketing and search engine optimization',
      details: [
        'Search Engine Optimization (SEO)',
        'Pay-Per-Click (PPC) Advertising',
        'Social Media Marketing',
        'Content Marketing',
        'Local SEO',
        'Technical SEO Audit'
      ]
    },
    {
      id: 6,
      icon: 'fas fa-palette',
      title: 'Graphic Design',
      description: 'Creative design solutions that enhance your brand identity',
      shortDesc: 'Creative design solutions that enhance your brand identity',
      details: [
        'Brand Identity Design',
        'Logo Design',
        'Marketing Materials',
        'Social Media Graphics',
        'UI/UX Design',
        'Print Design'
      ]
    },
    {
      id: 7,
      icon: 'fas fa-server',
      title: 'Free Hosting & Support',
      description: 'Reliable hosting solutions with 24/7 technical support',
      shortDesc: 'Reliable hosting solutions with 24/7 technical support',
      details: [
        'Web Hosting Services',
        'Domain Management',
        'SSL Certificates',
        '24/7 Technical Support',
        'Regular Backups',
        'Performance Monitoring'
      ]
    }
  ];

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  // Simple animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const detailsVariants = {
    hidden: { 
      opacity: 0, 
      height: 0
    },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="py-5 bg-light">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-5">
          <h2 className="h1 fw-bold text-dark mb-3">Our Services</h2>
          <p className="lead text-muted">Comprehensive solutions to accelerate your business growth</p>
        </div>

        {/* Services Grid */}
        <div className="row g-4">
          {services.map((service, index) => (
            <motion.div 
              className="col-12 col-sm-6 col-lg-4" 
              key={service.id}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <motion.div 
                className="card h-100 shadow-sm border-0 d-flex flex-column"
                style={{
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                whileHover={{ 
                  y: -5,
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="card-body p-4 d-flex flex-column flex-grow-1">
                  <div className="text-center mb-3">
                    <i className={`${service.icon} fa-2x text-primary mb-3`}></i>
                    <h5 className="card-title fw-bold">{service.title}</h5>
                  </div>
                  
                  <p className="card-text text-muted mb-4">{service.shortDesc}</p>

                  <AnimatePresence>
                    {expandedService === service.id && (
                      <motion.div 
                        className="service-details mb-4"
                        variants={detailsVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                      >
                        <p className="text-muted mb-3">{service.description}</p>
                        <h6 className="fw-bold mb-3 text-primary">What's Included:</h6>
                        <ul className="list-unstyled">
                          {service.details.map((detail, idx) => (
                            <li key={idx} className="mb-2 d-flex align-items-center">
                              <i className="fas fa-check text-success me-2"></i>
                              <span className="small">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-auto text-center">
                    <motion.button
                      onClick={() => toggleService(service.id)}
                      className={`btn ${
                        expandedService === service.id 
                          ? 'btn-outline-primary' 
                          : 'btn-primary'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {expandedService === service.id ? 'Show Less' : 'Learn More'}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-5" onClick={()=>navigate('/contact')}>
          <p className="lead mb-4">Ready to get started?</p>
          <motion.button 
            className="btn btn-primary btn-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Contact Us Today
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default ServicesGridSection;
